// Yield columns for crews and tools.
//
// The desktop stores every yield in imperial units (YieldSF, YieldLF, ...)
// and, for a metric version, shows and edits them converted
// (CrewDatabaseModel.Yield*ForBinding, UnitsNet). The factors below are the
// UnitsNet ones, so a value typed on the web lands in the database exactly
// as the desktop would store it.

export const YIELD_FIELDS = ["SF", "LF", "CY", "SY", "TON", "LB", "EA"];

const METRIC_LABEL = { SF: "SM", LF: "LM", CY: "CM", SY: "SM", TON: "TONNE", LB: "KG", EA: "EA" };

// UnitsNet 4.112, the package the desktop uses: a value goes to the base
// unit and from there to the target, with UnitsNet's own factors
// (SquareFoot 0.092903 m2, SquareYard 0.836127 m2, CubicYard 0.764554858 m3,
// ShortTon 907.18474 kg). new Area(v, SquareFoot).As(SquareMeter) etc.
const TO_METRIC = {
  SF: (v) => v * 0.092903,
  LF: (v) => v * 0.3048,
  CY: (v) => v * 0.764554858,
  SY: (v) => v * 0.836127,
  TON: (v) => (v * 9.0718474e2) / 1e3,
  LB: (v) => v * 0.45359237,
  EA: (v) => v,
};
const TO_IMPERIAL = {
  SF: (v) => v / 0.092903,
  LF: (v) => v / 0.3048,
  CY: (v) => v / 0.764554858,
  SY: (v) => v / 0.836127,
  TON: (v) => (v * 1e3) / 9.0718474e2,
  LB: (v) => v / 0.45359237,
  EA: (v) => v,
};

// UnitsProvider.DivisionUnitMap (imperial -> metric division unit).
export const DIVISION_UNIT_MAP = { SF: "SM", LF: "LM", CY: "CM", SY: "SM", TON: "TONNE", LB: "KG" };

// UnitsProvider.GetMappedDivisionUnit(unit, Metric).
export const mappedMetricUnit = (unit) => DIVISION_UNIT_MAP[unit] || unit;

// UnitsProvider.GetUnitConversionMultiplier(source, mapped target): the
// cost per imperial unit times this is the cost per metric unit, as
// 1 / (1 imperial unit in the metric unit). No rounding.
export function unitConversionMultiplier(source, target) {
  if (DIVISION_UNIT_MAP[source] && DIVISION_UNIT_MAP[source] === target) return 1 / TO_METRIC[source](1);
  const back = Object.keys(DIVISION_UNIT_MAP).find((k) => DIVISION_UNIT_MAP[k] === source && k === target);
  if (back) return 1 / TO_IMPERIAL[back](1);
  return 1;
}

export const isMetricTemplate = (template) =>
  String((template && template.Measurements) || "").toLowerCase() === "metric";

export const yieldUnitLabel = (unit, metric) => (metric ? METRIC_LABEL[unit] : unit);

// Stored (imperial) -> shown.
export function yieldToDisplay(unit, value, metric) {
  const v = Number(value) || 0;
  return metric ? TO_METRIC[unit](v) : v;
}

// Shown -> stored (imperial).
export function yieldFromDisplay(unit, value, metric) {
  const v = Number(value) || 0;
  return metric ? TO_IMPERIAL[unit](v) : v;
}

// Crew.UpdateYield: the yield for the crew's working unit.
export function yieldForUnit(rec, unit) {
  const r = rec || {};
  switch (unit) {
    case "SF":
    case "LF":
    case "CY":
    case "SY":
    case "TON":
    case "LB":
    case "EA":
      return Number(r[`Yield${unit}`]) || 0;
    case "SM": {
      const sm = TO_METRIC.SF(Number(r.YieldSF) || 0);
      return sm > 0 ? sm : TO_METRIC.SY(Number(r.YieldSY) || 0);
    }
    case "LM":
      return TO_METRIC.LF(Number(r.YieldLF) || 0);
    case "CM":
      return TO_METRIC.CY(Number(r.YieldCY) || 0);
    case "TONNE":
      return TO_METRIC.TON(Number(r.YieldTON) || 0);
    case "KG":
      return TO_METRIC.LB(Number(r.YieldLB) || 0);
    default:
      return 0;
  }
}

// TotalPriceCalcVisitor.Visit(Crew): a crew's labour cost for one hour is
// the sum of Percentage x UnitCost over its labours (labor.TotalTime =
// crew.TotalTime * labor.Percentage), and the cost per unit of work is that
// divided by the crew's yield.
export function crewHourlyCost(labors) {
  return (labors || []).reduce(
    (sum, l) => sum + (Number(l.Percentage) || 0) * (Number(l.UnitCost) || 0),
    0
  );
}

export function crewCostPerUnit(crew, labors, unit) {
  const y = yieldForUnit(crew, unit);
  if (!y) return 0;
  return crewHourlyCost(labors) / y;
}

// Crew.GetTotalPercentage.
export const crewTotalPercentage = (labors) =>
  (labors || []).reduce((s, l) => s + (Number(l.Percentage) || 0), 0);
